'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import cls from './FadeOnScroll.module.scss';
import classNames from 'classnames';

export type FadeOnScrollProps = {
  children: React.ReactNode;
  scrollDirection?: 'vertical' | 'horizontal';
};

type ScrollState = {
  end: boolean;
  start: boolean;
  isScrollable: boolean;
};

export const FadeOnScroll = ({
  children,
  scrollDirection = 'vertical',
}: FadeOnScrollProps) => {
  const [classStart, setClassStart] = useState('');
  const [classEnd, setClassEnd] = useState('');
  const [scrollTargetElement, setScrollTargetElement] =
    useState<HTMLElement | null>(null);

  const fadeOnScrollContainerRef = useRef<HTMLDivElement>(null);

  const toggleClass = useCallback(
    (scrollState: ScrollState) => {
      if (!scrollState['isScrollable']) {
        setClassStart('');
        setClassEnd('');
      }

      if (scrollDirection === 'vertical') {
        setClassStart(scrollState.start ? '' : cls.topOverflow);
        setClassEnd(scrollState.end ? '' : cls.bottomOverflow);
      } else {
        setClassStart(scrollState.start ? '' : cls.leftOverflow);
        setClassEnd(scrollState.end ? '' : cls.rightOverflow);
      }
    },
    [scrollDirection, setClassStart, setClassEnd],
  );

  useEffect(() => {
    if (fadeOnScrollContainerRef) {
      setScrollTargetElement(fadeOnScrollContainerRef.current as HTMLElement);
    }
  }, [fadeOnScrollContainerRef]);

  useEffect(() => {
    const handleScroll = () => {
      if (scrollTargetElement) {
        toggleClass(fadeOnScrolling(scrollTargetElement, scrollDirection));
      }
    };
    if (scrollTargetElement) {
      scrollTargetElement.addEventListener('scroll', handleScroll);
      handleScroll();

      return () => {
        scrollTargetElement.removeEventListener('scroll', handleScroll);
      };
    }
  }, [scrollTargetElement, scrollDirection, toggleClass]);

  return (
    <div
      className={classNames(
        cls.fadingMask,
        scrollDirection === 'vertical' ? cls.vertical : cls.horizontal,
        classStart,
        classEnd,
      )}
      data-qa="fading-element"
      ref={fadeOnScrollContainerRef}
    >
      {children}
    </div>
  );
};

const fadeOnScrolling = (
  el: HTMLElement,
  scrollDir: 'vertical' | 'horizontal',
): ScrollState => {
  const elementClientSize =
    scrollDir === 'vertical' ? el.clientHeight : el.clientWidth;
  const elementScrollSize =
    scrollDir === 'vertical' ? el.scrollHeight : el.scrollWidth;
  const scrollPosition =
    scrollDir === 'vertical' ? el.scrollTop : el.scrollLeft;

  const isScrollable = elementScrollSize > elementClientSize;

  const isScrolledToEnd =
    elementScrollSize < elementClientSize + scrollPosition + 1;
  const isScrolledToStart = isScrolledToEnd ? false : scrollPosition === 0;

  return {
    end: isScrollable ? isScrolledToEnd : true,
    start: isScrollable ? isScrolledToStart : true,
    isScrollable: isScrollable,
  };
};
